import {
    PaymentRequestTableRecord,
    PaymentRequestType,
    useInvalidateRepositoryQuery,
    useRepositoryMutation,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';
import { LocalizedText, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { comparePaymentRequestByCreatedUtc } from './utils';

export const useGetAllPaymentRequests = ({ accountId }: WithAccountId) =>
    useRepositoryQuery(
        'paymentRequests',
        'getTableRecords',
        { accountId },
        {
            select: (result) => result.toSorted(comparePaymentRequestByCreatedUtc) ?? [],
        }
    );

export const useCancelPaymentRequest = ({
    accountId,
    paymentRequest: { amount, type },
}: { paymentRequest: PaymentRequestTableRecord } & WithAccountId) => {
    const invalidateGetAllPaymentRequests = useInvalidateGetAllPaymentRequests();

    return useRepositoryMutation('paymentRequests', 'cancel', {
        onSuccess: () => {
            showSuccessAlert({
                description:
                    type === PaymentRequestType.PaymentMethod ? (
                        <LocalizedText textKey={TextKey.PaymentMethodRequestCancelledSuccessMessage} />
                    ) : (
                        <LocalizedText
                            textKey={TextKey.PaymentRequestCancelledSuccessMessage}
                            substitutes={
                                amount
                                    ? {
                                          amount: localizeCurrencyValue({
                                              value: amount.value,
                                              currency: amount.currency,
                                          }),
                                      }
                                    : undefined
                            }
                        />
                    ),
            });

            invalidateGetAllPaymentRequests({ accountId });
        },
    });
};

export const useInvalidateGetAllPaymentRequests = () => {
    const invalidateQuery = useInvalidateRepositoryQuery();

    return ({ accountId }: WithAccountId) => void invalidateQuery('paymentRequests', 'getTableRecords', { accountId });
};
